import React, { Component } from 'react';
import axios from 'axios';
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { withRouter } from 'react-router-dom';
import { sortBy, find, findIndex, clone } from 'lodash';
import { CSVLink } from "react-csv";
// material
import { Container, Hidden } from '@material-ui/core';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
// app
import { updateSession, setApiUrls, addAuth, openSnackbar, closeSnackbar } from '../../store/action-creators';
// containers
import AppContainer from '../../containers/App';
// models
import { Auth, Session } from '../../entities/auth';
import { APIUrl, Campaign, emptyCampaign, CampaignContact, CampignContactFavorite, emptyCampaignContact, campaignContactStatusFilters } from '../../entities/site';
import { MessageGroup, Message, emptyMessageGroup } from '../../entities/notification';
import { Address } from '../../entities/home';
// components
import Loading from '../../components/site/Loading';
import CampaignService from '../../services/campaign';
import CampaignHeader from './components/CampaignHeader';
import CampaignContactHeader from './components/CampaignContactHeader';
import CampaignContactPanel from './components/CampaignContactPanel';
import CustomTab from '../../components/tabs/CustomTab';
import CustomTabs from '../../components/tabs/CustomTabs';
import ActionButton from '../../components/buttons/ActionButton';
import EditCampaignContact from '../../components/campaign/EditCampaignContact';
import BackButton from '../../components/buttons/BackButton';
import SelectInput from '../../components/inputs/SelectInput';
// services
import AuthService from '../../services/auth';
import UtilsService from '../../services/utils';
import { mergeClasses } from '@material-ui/styles';

const useProd = true;

const styles: any = {
  campaignTitle: {
    fontSize: 14
  }
}

export interface ContactData {
  id: string;
  contact: CampaignContact;
  loading: boolean;
  messageGroup: MessageGroup;
  address: Address | null;
  messages: Message[];
}

interface CampaignPageProps {
  match?: any;
  history: any;
  auth: Auth;
  session: Session;
  apiUrls: APIUrl;
  updateSession(session: Session): void;
  setApiUrls(urls: APIUrl): void;
  openSnackbar(message: string): void;
  closeSnackbar(): void;
}

interface CampaignPageState {
  loading: boolean;
  campaign: Campaign;
  campaignContacts: CampaignContact[];
  campaignContactsOptOuts: CampaignContact[];
  campaignContactsClickThroughs: CampaignContact[];
  contactData: ContactData[];
  status: string;
  lastKey: any;
  openEditCampaignContact: boolean;
  campaignContactId: string;
}

class CampaignPage extends Component<CampaignPageProps, CampaignPageState> {

  constructor(props: CampaignPageProps) {
    super(props);
    this.state = {
      loading: false,
      campaign: emptyCampaign,
      campaignContacts: [],
      campaignContactsOptOuts: [],
      campaignContactsClickThroughs: [],
      contactData: [],
      status: 'all',
      lastKey: null,
      openEditCampaignContact: false,
      campaignContactId: ''
    }
  }

  componentDidMount = async () => {
    const campaignId = this.props.match.params.id;
    this.fetchCampaign(campaignId);
    this.fetchCampaignContacts(campaignId, this.state.status, null);
  }

  fetchCampaign = async (campaignId: string) => {
    // fetch
    const campaignRes = await axios.get(`${AuthService.getApiUrls().site}/campaign/${campaignId}`);
    this.setState({
      campaign: campaignRes.data,
    });
  }

  fetchCampaignContacts = async (campaignId: string, status: string, lastKey: any) => {

    try {

      this.setState({ loading: true });

      let url = `${AuthService.getApiUrls().site}/campaign/${campaignId}/contacts?limit=250`;
      if (status !== 'all') {
        url += `&status=${status}`;
      }
      if (lastKey) {
        url += `&id=${lastKey.id}&campaignId=${lastKey.campaignId}`
      }
      const campaignContactsRes = await axios.get(url);

      let contacts = this.state.campaignContacts;
      contacts = contacts.concat(campaignContactsRes.data.results);
      contacts = sortBy(contacts, 'firstName');

      this.setState({
        loading: false,
        campaignContacts: contacts,
        lastKey: campaignContactsRes.data.lastKey,
      });

    } catch (error) {
      this.setState({ loading: false });
    }

  }

  fetchFavorites = async (campaignId: string, lastKey: any) => {

    try {

      this.setState({ loading: true });

      const user = AuthService.getUser();
      const favoritesRes = await axios.get(`${AuthService.getApiUrls().site}/campaign/contacts/favorites/campaign/${campaignId}/user/${user?.userId}`);

      let contacts = this.state.campaignContacts;
      favoritesRes.data.results.forEach((favorite: CampignContactFavorite) => {
        const newCampaignContact = clone(emptyCampaignContact);
        newCampaignContact.id = favorite.contactId;
        newCampaignContact.campaignId = favorite.campaignId;
        newCampaignContact.email = favorite.contactEmail;
        newCampaignContact.phoneNumber = favorite.contactPhone;
        newCampaignContact.firstName = favorite.contactFirstName;
        newCampaignContact.lastName = favorite.contactLastName;
        newCampaignContact.favoriteId = `${user?.userId}|${favorite.contactId}|${favorite.id}`;
        newCampaignContact.createdDate = favorite.updatedDate;
        newCampaignContact.newMessage = favorite.newMessage || false;
        contacts.push(newCampaignContact);
      });
      contacts = sortBy(contacts, 'createdDate');

      this.setState({
        loading: false,
        campaignContacts: contacts,
        lastKey: favoritesRes.data.lastKey,
      });


    } catch (error) {
      this.setState({ loading: false });
    }

  }


  handleLoadMore = async () => {
    // load more
    await this.fetchCampaignContacts(this.state.campaign.id, this.state.status, this.state.lastKey);
  }

  handleRoute = (campaign: Campaign) => {
    this.props.history.push(`/admin/campaign/${campaign.id}`);
  }

  handleChange = async (contact: CampaignContact, isMobile: boolean) => {

    if (isMobile) {
      this.props.history.push(`/campaign/${contact.campaignId}/contact/${contact.id}`);
    } else {
      this.fetchMessageGroup(contact, false);
    }

  }

  fetchMessageGroup = async (contact: CampaignContact, refresh: boolean) => {

    try {

      const data = find(this.state.contactData, { id: contact.id });
      if (!data || refresh) {

        const newData: ContactData = {
          id: contact.id,
          loading: true,
          contact,
          messageGroup: emptyMessageGroup,
          address: null,
          messages: []
        };

        if (!refresh) {
          // not loaded, query message group from contact
          this.setState({ contactData: [...this.state.contactData, newData] });
        }

        // get message group by contact id
        const messageGroupRes = await axios.get(`${AuthService.getApiUrls().notifications}/message/group/contact/${contact.id}`);
        if (messageGroupRes) {
          newData.messageGroup = messageGroupRes.data;
          // get messages
          const messages = await axios.get(`${AuthService.getApiUrls().notifications}/messages/group/${messageGroupRes.data.id}`);
          let sortedMessages = sortBy(messages.data, 'createdDate');
          sortedMessages = sortedMessages.reverse();
          newData.messages = sortedMessages;

          newData.loading = false;
          // update cache
          const updateDate = [...this.state.contactData];
          const updateIndex = findIndex(this.state.contactData, { id: contact.id });
          updateDate[updateIndex] = newData;
          this.setState({ contactData: updateDate });

        }

      }

    } catch (error) {
      const updateData = [...this.state.contactData];
      const updateIndex = findIndex(this.state.contactData, { id: contact.id });
      const newData = { ...updateData[updateIndex], loading: false };
      updateData[updateIndex] = newData;
      this.setState({ contactData: updateData });
    }

  }

  showLoader = (contact: CampaignContact) => {
    const data = find(this.state.contactData, { id: contact.id });
    if (data && data.loading) {
      return true;
    }
    return false;
  }

  handleSendMessage = async (contact: CampaignContact, message: string) => {

    this.setState({ loading: true });

    // send message
    const data = find(this.state.contactData, { id: contact.id });
    if (data && data.messageGroup && message) {

      const group = data.messageGroup;
      const messageRes = await CampaignService.sendMessage(
        AuthService.getApiUrls().notifications,
        this.props.auth.user,
        group.id,
        group.toPhoneNumber,
        group.fromPhoneNumber,
        message
      );

      this.setState({ loading: false });

      if (messageRes) {
        this.fetchMessageGroup(contact, true);
      }

    }

  }

  handleRefresh = (contact: CampaignContact) => {
    this.handleSetContactLoading(contact);
    this.fetchMessageGroup(contact, true);
  }

  handleEdit = (contact: CampaignContact) => {

    console.log('edit', contact);

    // load edit form
    this.setState({
      campaignContactId: contact.id,
      openEditCampaignContact: true
    });
  }

  handleOpen = (contact: CampaignContact) => {
    this.props.history.push(`/campaign/${contact.campaignId}/contact/${contact.id}`);
  }

  handleEditCampaignContactClose = async () => {
    this.setState({
      openEditCampaignContact: false
    });
    await this.fetchCampaignContacts(this.state.campaign.id, this.state.status, this.state.lastKey);
  }

  handlePropertyLoad = async (contact: CampaignContact) => {

    try {

      this.handleSetContactLoading(contact);

      const addressStr = `${contact.address}, ${contact.city}, ${contact.state}`;
      const addressId = UtilsService.getKeyFromAddress(addressStr);
      const updateIndex = findIndex(this.state.contactData, { id: contact.id });
      const updateData = [...this.state.contactData];

      // look in cache
      const addressRes = await axios.get(`${AuthService.getApiUrls().home}/address/${addressId}`);
      if (addressRes.data) {
        updateData[updateIndex] = { ...updateData[updateIndex], address: addressRes.data, loading: false };
        this.setState({ contactData: updateData });
      } else {
        const propertyStatsRes = await axios.get(`${AuthService.getApiUrls().integrations}/property/calculate/?address=${contact.address}&city=${contact.city}&state=${contact.state}`);
        if (propertyStatsRes) {
          updateData[updateIndex] = { ...updateData[updateIndex], address: addressRes.data, loading: false };
          const addressNew = propertyStatsRes.data;
          addressNew.id = addressId;
          this.setState({ contactData: updateData });
          await axios.post(`${this.props.apiUrls.home}/address`, addressNew);
        }
      }

    } catch (error) {
      const updateIndex = findIndex(this.state.contactData, { id: contact.id });
      const updateData = [...this.state.contactData];
      updateData[updateIndex] = { ...updateData[updateIndex], address: null, loading: false };
      this.setState({ contactData: updateData });
    }

  }

  handleFavorite = async (contact: CampaignContact) => {

    try {

      this.setState({ loading: true });

      const user = AuthService.getUser();
      const searchFavoriteId = `${user?.userId}|${contact.id}`;

      if (!contact.favoriteId || contact.favoriteId.indexOf(searchFavoriteId) === -1) {

        // create
        const data = {
          userId: user?.userId,
          campaignId: this.state.campaign.id,
          campaignName: this.state.campaign.name,
          contactId: contact.id,
          contactFirstName: contact.firstName,
          contactLastName: contact.lastName,
          contactEmail: contact.email,
          contactPhone: contact.phoneNumber,
          newMessage: contact.newMessage || false
        }

        const favoriteRes = await axios.post(`${AuthService.getApiUrls().site}/campaign/contact/favorite`, data);

        // update contact
        contact.favoriteId = (contact.favoriteId !== "null" ? contact.favoriteId : "") + `${searchFavoriteId}|${favoriteRes.data.id},`;
        await axios.put(`${AuthService.getApiUrls().site}/campaign/contact/${contact.id}`, contact);

        // update list
        const updateIndex = findIndex(this.state.contactData, { id: contact.id });
        const updateData = [...this.state.contactData];
        updateData[updateIndex] = { ...updateData[updateIndex], contact };
        this.setState({ contactData: updateData, loading: false });

      } else {

        let newFavoriteIds = '';
        let favoriteId = '';
        const parts = contact.favoriteId.split(',');

        if (parts.length) {
          parts.forEach((fullId: string) => {
            if (fullId && fullId.indexOf(searchFavoriteId) === -1) {
              newFavoriteIds = newFavoriteIds + `${fullId},`;
            }
            if (fullId.indexOf(searchFavoriteId) !== -1) {
              const idParts = fullId.split('|');
              favoriteId = idParts[2];
            }
          });
        }

        // fetch refresh
        const contactNewRes = await axios.get(`${AuthService.getApiUrls().site}/campaign/contact/${contact.id}`);
        const contactNew = contactNewRes.data;

        // update contact
        contactNew.favoriteId = newFavoriteIds || 'null';
        await axios.put(`${AuthService.getApiUrls().site}/campaign/contact/${contact.id}`, contactNew);

        // delete
        await axios.delete(`${AuthService.getApiUrls().site}/campaign/contact/favorite/${favoriteId}`);

        // update list
        const updateIndex = findIndex(this.state.contactData, { id: contact.id });
        const updateData = [...this.state.contactData];
        updateData[updateIndex] = { ...updateData[updateIndex], contact };
        this.setState({ contactData: updateData, loading: false });

      }

    } catch (error) {
      console.log('error', error);
      this.setState({ loading: false });
    }


  }

  handleSetContactLoading = (contact: CampaignContact) => {
    const updateIndex = findIndex(this.state.contactData, { id: contact.id });
    const updateData = [...this.state.contactData];
    updateData[updateIndex] = { ...updateData[updateIndex], loading: true };
    this.setState({ contactData: updateData });
  }

  handleTabChange = (newValue: string) => {
    this.setState({
      status: newValue,
      lastKey: null,
      campaignContacts: []
    });
    if (newValue === 'favorites') {
      this.fetchFavorites(this.state.campaign.id, null);
    } else {
      this.fetchCampaignContacts(this.state.campaign.id, newValue, null);
    }
  }

  a11yProps = (index: any) => {
    return {
      id: `simple-tab-${index}`,
      'aria-controls': `simple-tabpanel-${index}`,
    };
  }

  render() {

    const today = new Date().toLocaleDateString();

    return (
      <AppContainer showOption={false} private={false} maxWidth={false}>
        <Loading open={this.state.loading} />
        <Container maxWidth="lg">
          <BackButton text="Back to campaign" onClick={() => this.props.history.push(`/campaigns`)} />
          <Hidden smDown>
            <CampaignHeader campaign={this.state.campaign} />
          </Hidden>
          <Hidden mdUp>
            <div style={styles.campaignTitle}>{this.state.campaign.name}</div>
          </Hidden>
          <Hidden smDown>
            <div style={{ marginBottom: 10 }}>
              <CustomTabs value={this.state.status} onChange={(event: any, value: string) => this.handleTabChange(value)} aria-label="contact statuses">
                <CustomTab value="all" label="All" {...this.a11yProps('all')} />
                <CustomTab value="active" label="Replies" {...this.a11yProps('active')} />
                <CustomTab value="clickThrough" label="Click Throughs" {...this.a11yProps('clickThrough')} />
                <CustomTab value="application" label="Applications" {...this.a11yProps('clickThrough')} />
                <CustomTab value="sent" label="Sent " {...this.a11yProps('sent')} />
                <CustomTab value="optOut" label="Opt Outs" {...this.a11yProps('optOut')} />
                <CustomTab value="favorites" label="Favorites" {...this.a11yProps('favorites')} />
              </CustomTabs>
            </div>
          </Hidden>
          <Hidden mdUp>
            <SelectInput
              title=""
              value={this.state.status}
              values={campaignContactStatusFilters}
              onChange={(event: any) => this.handleTabChange(event.target.value)}
              fullWidth
            />
          </Hidden>
          <div style={{ marginBottom: 80 }}>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <CSVLink
                data={this.state.campaignContacts}
                filename={`campaign-contacts-${today}.csv`}
                className="csv-link"
              >Export to cvs</CSVLink>
            </div>
            <CampaignContactHeader />
            {this.state.campaignContacts.map((contact, index) => (
              <CampaignContactPanel
                key={`all-${index}`}
                contact={contact}
                campaign={this.state.campaign}
                contactData={this.state.contactData}
                showLoader={this.showLoader(contact)}
                onPanelOpen={this.handleChange}
                onSendMessage={this.handleSendMessage}
                onRefresh={this.handleRefresh}
                onEdit={this.handleEdit}
                onOpen={this.handleOpen}
                onPropertyLoad={this.handlePropertyLoad}
                onFavorite={this.handleFavorite}
              />
            ))}
            {
              this.state.lastKey !== null &&
              <ActionButton name="LOAD MORE" onClick={this.handleLoadMore} fullWidth />
            }
          </div>
        </Container>
        <EditCampaignContact
          open={this.state.openEditCampaignContact}
          campaignContactId={this.state.campaignContactId}
          onClose={this.handleEditCampaignContactClose}
        />
      </AppContainer>
    )
  }

}

const mapStateToProps = (state: any) => {
  return {
    auth: state.auth.auth,
    session: state.session.session,
    apiUrls: state.site.apiUrls
  };
};

const mapDispatchToProps = (dispatch: any) => {
  return bindActionCreators({ updateSession, setApiUrls, addAuth, openSnackbar, closeSnackbar }, dispatch);
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withRouter<any, any>(CampaignPage));


// WEBPACK FOOTER //
// ./src/pages/campaign/Campaign.tsx